package com.essntl.features.itinerary.ui.edit_itinerary.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import core.dateformat.utils.formatTimestampToHHMM
import com.essntl.core.utils.utils.capitalizeFirstLetter
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.ui.edit_itinerary.MessageSendType
import com.essntl.features.message.domain.model.MessageModel
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.features.supplier.domain.model.SupplierModel
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime

@Composable
fun rememberSortedItineraryServices(
    itineraryServices: List<ItineraryServiceModel>,
    openDialog: Boolean = true,
): List<ItineraryServiceModel> =
    remember(itineraryServices, openDialog) {
        itineraryServices
            .sortedWith(
                compareBy<ItineraryServiceModel> {
                    LocalDate.parse(it.startDate)
                }.thenBy {
                    it.startTime?.let { time ->
                        LocalTime.parse(formatTimestampToHHMM(time))
                    }
                },
            )
    }

@Composable
fun rememberSupplier(
    itineraryServices: List<ItineraryServiceModel>,
    openDialog: Boolean = true,
): List<SupplierModel?> =
    remember(itineraryServices, openDialog) {
        itineraryServices
            .map { it.supplier }
            .toSet()
            .toList()
    }

@Composable
fun rememberPopupTitle(
    client: ClientModel,
    supplier: SupplierModel?,
    travelAgent: TravelAgentModel?,
    type: MessageSendType,
    openDialog: Boolean = true,
): String =
    remember(client, supplier, travelAgent, type, openDialog) {
        when (type) {
            MessageSendType.SUPPLIER_PO -> {
                "Purchase order - ${supplier?.prettyName}"
            }

            MessageSendType.SUPPLIER_GENERIC -> {
                "Contact ${supplier?.prettyName}"
            }

            MessageSendType.CLIENT -> {
                "Contact ${client.prettyName}"
            }

            MessageSendType.TRAVEL_AGENT -> {
                "Contact ${travelAgent?.prettyName}"
            }

        }.capitalizeFirstLetter()
    }

@Composable
fun rememberEmailEnabled(
    client: ClientModel,
    supplier: SupplierModel?,
    travelAgent: TravelAgentModel?,
    type: MessageSendType,
    openDialog: Boolean = true,
): Boolean =
    remember(client, supplier, travelAgent, type, openDialog) {
        type.isSupplierMessage && supplier != null && !supplier.contact?.email.isNullOrEmpty() ||
            type == MessageSendType.CLIENT && !client.email.isNullOrEmpty() ||
            type == MessageSendType.TRAVEL_AGENT && !travelAgent?.contact?.email.isNullOrEmpty()
    }

@Composable
fun rememberPhoneNumberEnabled(
    client: ClientModel,
    supplier: SupplierModel?,
    travelAgent: TravelAgentModel?,
    type: MessageSendType,
    openDialog: Boolean = true,
): Boolean =
    remember(client, supplier, type, openDialog) {
        (type.isSupplierMessage && supplier != null && !supplier.contact?.phoneNumber.isNullOrEmpty()) ||
            type == MessageSendType.CLIENT && !client.telephone.isNullOrEmpty() ||
            type == MessageSendType.TRAVEL_AGENT && !travelAgent?.contact?.phoneNumber.isNullOrEmpty()
    }

@Composable
fun rememberServiceInfosMessagesTemplate(
    categoryList: List<CategoryModel>,
    itineraryServices: List<ItineraryServiceModel>,
    client: ClientModel?,
    profile: ProfileModel?,
    type: MessageSendType,
    openDialog: Boolean = true,
): List<String> =
    remember(categoryList, itineraryServices, client, profile, openDialog) {
        when (type) {
            MessageSendType.SUPPLIER_PO, MessageSendType.SUPPLIER_GENERIC, MessageSendType.TRAVEL_AGENT ->
                itineraryServices.map { proposalService ->

                    MessageModel.messageStringBuilder(
                        service = proposalService.service,
                        itineraryService = proposalService,
                        client = client,
                        forClient = false,
                        forSupplierTravelAgent = type != MessageSendType.SUPPLIER_PO,
                    )
                }

            MessageSendType.CLIENT ->
                if (itineraryServices.size == 1) {
                    val service = itineraryServices
                        .firstOrNull()
                        ?.service
                        ?: return@remember listOf("")

                    val category = categoryList.firstOrNull { it.id == service.category }
                        ?: return@remember listOf("")

                    if (itineraryServices.firstOrNull() != null) {
                        listOf(
                            MessageModel.getClientTomorrowMessageString(
                                client = client,
                                service = service,
                                itineraryService = itineraryServices.first(),
                                category = category,
                                sender = profile,
                            ),
                        )
                    } else {
                        listOf("")
                    }
                } else {
                    listOf(
                        MessageModel.messageStringBuilder(
                            itineraryServiceList = itineraryServices,
                            client = client,
                            forClient = true,
                            sender = profile,
                        ),
                    )
                }
        }
    }

@Composable
fun rememberIsEmailChecked(
    type: MessageSendType,
    emailCheckBoxEnabled: Boolean,
): MutableState<Boolean> =
    remember(type) {
        mutableStateOf(
            (type.isSupplierMessage ||
                type == MessageSendType.TRAVEL_AGENT) &&
                emailCheckBoxEnabled,
        )
    }

@Composable
fun rememberIsWhatsappChecked(
    type: MessageSendType,
    messageCheckBoxEnabled: Boolean,
): MutableState<Boolean> =
    remember(type) {
        mutableStateOf(type == MessageSendType.CLIENT && messageCheckBoxEnabled)
    }

@Composable
fun rememberTypedMessage(
    type: MessageSendType,
    serviceInfosMessagesTemplate: List<String>,
): MutableState<String> =
    remember(type, serviceInfosMessagesTemplate) {
        when (type) {
            MessageSendType.CLIENT -> mutableStateOf(serviceInfosMessagesTemplate.first())
            MessageSendType.SUPPLIER_GENERIC, MessageSendType.TRAVEL_AGENT -> {
                val finalString = mutableStateOf<String>("")
                serviceInfosMessagesTemplate
                    .chunked(2)
                    .forEach { strings ->
                        strings.forEach { messageServiceInfos ->
                            finalString.value += messageServiceInfos
                        }
                    }
                finalString
            }

            else -> mutableStateOf("")
        }
    }

@Composable
fun rememberShowsServiceInfo(
    type: MessageSendType,
): Boolean =
    remember(type) {
        type == MessageSendType.SUPPLIER_PO
    }

@Composable
fun rememberEmail(
    type: MessageSendType,
    supplier: SupplierModel?,
    client: ClientModel?,
    travelAgent: TravelAgentModel?,
): String =
    remember(type, supplier, client) {
        if (type.isSupplierMessage) {
            supplier?.contact?.email ?: ""
        } else if (type == MessageSendType.CLIENT) {
            client?.email ?: ""
        } else {
            travelAgent?.contact?.email ?: ""
        }
    }

@Composable
fun rememberTelephone(
    type: MessageSendType,
    supplier: SupplierModel?,
    client: ClientModel?,
    travelAgent: TravelAgentModel?,
): String? =
    remember(type, supplier, client, travelAgent) {
        if (type.isSupplierMessage) {
            supplier?.contact?.phoneNumber
        } else if (type == MessageSendType.CLIENT) {
            client?.telephone
        } else {
            travelAgent?.contact?.phoneNumber
        }
    }

fun getItineraryMessages(
    categoryList: List<CategoryModel>,
    itineraryServiceList: List<ItineraryServiceModel>,
    isWhatsappChecked: Boolean,
    typedMessage: String,
    toTelephoneNumber: String,
): List<MessageModel> {
    val messagesList = mutableListOf<MessageModel>()
    itineraryServiceList.forEach { _ ->
        val message =
            MessageModel.empty.copy(
                userText = typedMessage,
                serviceList = itineraryServiceList.map { it.service },
                categoryList = categoryList,
                itineraryServiceList = itineraryServiceList,
            )

        if (isWhatsappChecked && toTelephoneNumber.isNotEmpty()) {
            messagesList.add(message.copy(type = MessageModel.Type.Whatsapp(toTelephoneNumber)))
        }
    }

    return messagesList
}